import React, { useState, useEffect, useCallback } from 'react';
import { isPreviewable } from '../../../../../../services/attachmentService';
import { formatFileSize } from '../../utils/formatters';
import { usePreviewData } from './hooks/usePreviewData';
import MediaPreview from './components/MediaPreview';
import CompareView from './components/CompareView';
import RestorePrompt from './components/RestorePrompt';

/**
 * PreviewPanel Component
 * 
 * Shows a preview of the selected file with:
 * - Image preview with zoom
 * - PDF preview via iframe
 * - Video/audio preview with native controls
 * - Version comparison with navigation through all versions
 * - Restore functionality with note prompt
 */
const PreviewPanel = ({
  missionId,
  file,
  compareVersion: initialCompareVersion,
  onClose,
  onRestore,
}) => {
  const [zoom, setZoom] = useState(1);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [restoreNote, setRestoreNote] = useState('');

  const {
    currentPreviewData,
    comparePreviewData,
    isLoading,
    error,
    olderVersions,
    hasVersions,
    currentCompareVersion,
    totalVersions,
    olderVersionNumber,
    isComparing,
    compareIndex,
    goToPrevVersion,
    goToNextVersion,
    toggleCompare,
  } = usePreviewData(missionId, file, initialCompareVersion);

  // Restore handlers
  const handleRestoreClick = useCallback(() => {
    setRestoreNote('');
    setShowRestorePrompt(true);
  }, []);

  const handleRestoreConfirm = useCallback(() => {
    if (currentCompareVersion && onRestore) {
      onRestore(file, currentCompareVersion, restoreNote);
    }
    setShowRestorePrompt(false);
    setRestoreNote('');
  }, [currentCompareVersion, file, onRestore, restoreNote]);

  const handleRestoreCancel = useCallback(() => {
    setShowRestorePrompt(false);
    setRestoreNote('');
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showRestorePrompt) return;
      
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onClose();
      } else if (e.key === '+' || e.key === '=') {
        setZoom(z => Math.min(z + 0.25, 3));
      } else if (e.key === '-') {
        setZoom(z => Math.max(z - 0.25, 0.25));
      } else if (e.key === '0') {
        setZoom(1);
      } else if (isComparing && hasVersions) {
        if (e.key === 'ArrowLeft') {
          goToPrevVersion();
        } else if (e.key === 'ArrowRight') {
          goToNextVersion();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose, isComparing, hasVersions, goToPrevVersion, goToNextVersion, showRestorePrompt]);

  const canPreview = isPreviewable(file.type);

  return (
    <div className="preview-panel">
      {/* Header */}
      <div className="preview-panel__header">
        <div className="preview-panel__title">
          <span className="preview-panel__filename">{file.filename}</span>
          <span className="preview-panel__meta">
            {formatFileSize(file.size)} • {totalVersions} version{totalVersions !== 1 ? 's' : ''}
          </span>
        </div>
        
        <div className="preview-panel__actions">
          {hasVersions && (
            <button 
              className={`preview-panel__compare-toggle ${isComparing ? 'preview-panel__compare-toggle--active' : ''}`}
              onClick={toggleCompare}
              title={isComparing ? 'Exit comparison' : 'Compare versions'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="18" rx="1" />
                <rect x="14" y="3" width="7" height="18" rx="1" />
              </svg>
              <span>{isComparing ? 'Exit Compare' : 'Compare'}</span>
            </button>
          )}

          {file.type === 'image' && (
            <div className="preview-panel__zoom">
              <button 
                className="preview-panel__zoom-btn"
                onClick={() => setZoom(z => Math.max(z - 0.25, 0.25))}
                title="Zoom out (-)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                </svg>
              </button>
              <span className="preview-panel__zoom-value">{Math.round(zoom * 100)}%</span>
              <button 
                className="preview-panel__zoom-btn"
                onClick={() => setZoom(z => Math.min(z + 0.25, 3))}
                title="Zoom in (+)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="11" y1="8" x2="11" y2="14" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                </svg>
              </button>
              <button 
                className="preview-panel__zoom-btn"
                onClick={() => setZoom(1)}
                title="Reset zoom (0)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          )}
          
          <button className="preview-panel__close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Preview area */}
      <div className={`preview-panel__body ${isComparing ? 'preview-panel__body--compare' : ''}`}>
        {isLoading ? (
          <div className="preview-panel__loading">
            <div className="preview-panel__spinner" />
            <span>Loading preview...</span>
          </div>
        ) : error ? (
          <div className="preview-panel__error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        ) : !canPreview ? (
          <div className="preview-panel__unsupported">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
              <polyline points="13 2 13 9 20 9" />
            </svg>
            <span>Preview not available for this file type</span>
            <span className="preview-panel__unsupported-hint">
              Download the file to view it
            </span>
          </div>
        ) : isComparing && currentCompareVersion ? (
          <CompareView
            file={file}
            currentCompareVersion={currentCompareVersion}
            currentPreviewData={currentPreviewData}
            comparePreviewData={comparePreviewData}
            compareIndex={compareIndex}
            olderVersions={olderVersions}
            totalVersions={totalVersions}
            olderVersionNumber={olderVersionNumber}
            zoom={zoom}
            onPrevVersion={goToPrevVersion}
            onNextVersion={goToNextVersion}
            onRestore={onRestore}
            onRestoreClick={handleRestoreClick}
          />
        ) : (
          <div className="preview-panel__single">
            <MediaPreview data={currentPreviewData} zoom={zoom} />
          </div>
        )}
      </div>

      {/* Keyboard hints */}
      {isComparing && hasVersions && (
        <div className="preview-panel__hints">
          <span>← → Navigate versions</span>
          <span>+/- Zoom</span>
          <span>Esc Close</span>
        </div>
      )}

      {/* Restore prompt modal */}
      {showRestorePrompt && (
        <RestorePrompt
          versionNumber={olderVersionNumber}
          note={restoreNote}
          onNoteChange={setRestoreNote}
          onConfirm={handleRestoreConfirm}
          onCancel={handleRestoreCancel}
        />
      )}
    </div>
  );
};

export default PreviewPanel;
